import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type TimerStatus = 'idle' | 'running' | 'paused'

export interface TimerState {
  // Core state
  status: TimerStatus
  projectId: string | null
  taskId: string | null
  description: string
  tagIds: string[]
  
  // Time tracking
  elapsedSeconds: number
  startedAt: string | null
  pausedAt: string | null
  
  // UI state
  isMinimized: boolean
  
  // Actions
  start: (projectId: string, taskId: string | null, description: string, tagIds?: string[]) => void
  pause: () => void
  resume: () => void
  stop: () => void
  cancel: () => void
  reset: () => void
  setDescription: (description: string) => void
  setProjectTask: (projectId: string, taskId: string | null) => void
  toggleMinimize: () => void
  incrementElapsed: () => void
  restoreFromStorage: (state: Partial<TimerState>) => void
  setStartedAt: (startedAt: string) => void
}

export const useTimerStore = create<TimerState>()(
  persist(
    (set, get) => ({
      status: 'idle',
      projectId: null,
      taskId: null,
      description: '',
      tagIds: [],
      elapsedSeconds: 0,
      startedAt: null,
      pausedAt: null,
      isMinimized: false,
      
      start: (projectId, taskId, description, tagIds = []) => {
        const now = new Date().toISOString()
        set({
          status: 'running',
          projectId,
          taskId,
          description,
          tagIds,
          startedAt: now,
          pausedAt: null,
          elapsedSeconds: 0,
        })
      },
      
      pause: () => {
        if (get().status !== 'running') return
        set({
          status: 'paused',
          pausedAt: new Date().toISOString(),
        })
      },
      
      resume: () => {
        if (get().status !== 'paused') return
        set({
          status: 'running',
          pausedAt: null,
        })
      },
      
      stop: () => {
        set({
          status: 'idle',
          pausedAt: null,
          startedAt: null,
        })
      },
      
      cancel: () => {
        set({
          status: 'idle',
          projectId: null,
          taskId: null,
          description: '',
          tagIds: [],
          elapsedSeconds: 0,
          startedAt: null,
          pausedAt: null,
        })
      },

      reset: () => {
        set({
          status: 'idle',
          projectId: null,
          taskId: null,
          description: '',
          tagIds: [],
          elapsedSeconds: 0,
          startedAt: null,
          pausedAt: null,
        })
      },
      
      setDescription: (description) => set({ description }),
      
      setProjectTask: (projectId, taskId) => set({ projectId, taskId }),
      
      toggleMinimize: () => set((state) => ({ isMinimized: !state.isMinimized })),
      
      incrementElapsed: () => {
        if (get().status === 'running') {
          set((state) => ({ elapsedSeconds: state.elapsedSeconds + 1 }))
        }
      },
      
      restoreFromStorage: (state) => {
        // Recalculate elapsed time from startedAt on reload
        if (state.status === 'running' && state.startedAt) {
          const now = new Date()
          const start = new Date(state.startedAt)
          state.elapsedSeconds = Math.floor((now.getTime() - start.getTime()) / 1000)
        }
        set(state)
      },

      setStartedAt: (startedAt) => {
        const { status } = get()
        if (status === 'running' && startedAt) {
          const now = new Date()
          const start = new Date(startedAt)
          const elapsed = Math.floor((now.getTime() - start.getTime()) / 1000)
          set({ startedAt, elapsedSeconds: Math.max(0, elapsed) })
        } else {
          set({ startedAt })
        }
      },
    }),
    {
      name: 'timer-state',
      partialize: (state) => ({
        status: state.status,
        projectId: state.projectId,
        taskId: state.taskId,
        description: state.description,
        tagIds: state.tagIds,
        elapsedSeconds: state.elapsedSeconds,
        startedAt: state.startedAt,
        pausedAt: state.pausedAt,
        isMinimized: state.isMinimized,
      }),
    }
  )
)

export default useTimerStore
